import React, { useRef, useState } from "react";
import { Camera, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "../lib/api";
import { getJwt } from "../lib/session";

/**
 * Mobile-friendly photo capture & viewer for QA audits.
 *
 * Props:
 *  - home, month, year (required)         → context for the audit
 *  - metricId         (optional)          → if set, photos attach to that metric (cap 3); else gallery (cap 20 audit-wide)
 *  - photos           (Array)             → current photos to render
 *  - onChange         (fn)                → called after add/delete to refresh parent state
 *  - canEdit          (bool)              → gate Capture/Delete; default true (parent should pass false for Staff)
 *  - maxPerMetric     (number)            → default 3
 */
export default function QAPhotoStrip({
  home, month, year, metricId = null,
  photos = [], onChange,
  canEdit = true,
  maxPerMetric = 3,
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [viewer, setViewer] = useState(null); // {url, caption, uploaded_by, uploaded_date}
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingCaption, setPendingCaption] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const limitReached = metricId ? photos.length >= maxPerMetric : false;

  // Resize/compress to ~1600px max edge, JPEG q=0.85, before upload.
  const compressImage = (file) => new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/") || file.type === "image/heic" || file.type === "image/heif") {
      // Skip canvas compression for HEIC (browsers can't decode it) — upload original.
      resolve(file); return;
    }
    const img = new window.Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      const MAX = 1600;
      let { width, height } = img;
      const scale = Math.min(MAX / width, MAX / height, 1);
      width = Math.round(width * scale); height = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("compression failed"));
        const out = new File([blob], (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
        resolve(out);
      }, "image/jpeg", 0.85);
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";   // allow re-picking same file
    if (!f) return;
    setPendingFile(f);
    setPendingCaption("");
  };

  const doUpload = async () => {
    if (!pendingFile) return;
    setBusy(true);
    try {
      const compressed = await compressImage(pendingFile);
      const fd = new FormData();
      fd.append("file", compressed);
      const params = new URLSearchParams({ home, month: String(month), year: String(year) });
      if (metricId) params.set("metric_id", metricId);
      if (pendingCaption.trim()) params.set("caption", pendingCaption.trim());
      await api.post(`/qa/photos?${params.toString()}`, fd);
      toast.success("Photo uploaded");
      setPendingFile(null); setPendingCaption("");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const doDelete = async (photoId) => {
    setBusy(true);
    try {
      await api.delete(`/qa/photos/${photoId}`);
      toast.success("Photo deleted");
      setConfirmDelete(null);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  // Auth-protected images: append ?auth= so <img> requests pass through /api/files.
  // image_url already starts with "/api/...", so concatenate the BACKEND origin (API_BASE minus its trailing "/api").
  const BACKEND_ORIGIN = API_BASE.replace(/\/api$/, "");
  const authedSrc = (url) => {
    if (!url) return "";
    const t = getJwt() || "";
    return `${BACKEND_ORIGIN}${url}${url.includes("?") ? "&" : "?"}auth=${encodeURIComponent(t)}`;
  };

  const testPrefix = metricId ? `qa-photo-metric-${metricId}` : "qa-photo-gallery";

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testPrefix}>
      {photos.map((p) => (
        <button
          key={p.id} type="button"
          onClick={() => setViewer(p)}
          className="relative group h-14 w-14 rounded-md overflow-hidden border border-slate-200 bg-slate-100 shrink-0 hover:ring-2 hover:ring-sky-400"
          data-testid={`${testPrefix}-thumb-${p.id}`}
          title={p.caption || "Photo"}
        >
          <img src={authedSrc(p.image_url)} alt="" className="h-full w-full object-cover" loading="lazy" />
        </button>
      ))}
      {canEdit && !limitReached && (
        <>
          <input
            ref={fileRef} type="file" accept="image/*" capture="environment"
            onChange={onFilePicked} className="hidden"
            data-testid={`${testPrefix}-input`}
          />
          <button
            type="button" onClick={() => fileRef.current?.click()} disabled={busy}
            className="h-14 w-14 inline-flex items-center justify-center rounded-md border-2 border-dashed border-slate-300 hover:border-sky-500 hover:bg-sky-50 text-slate-500 hover:text-sky-700 shrink-0 disabled:opacity-50"
            data-testid={`${testPrefix}-add-btn`}
            aria-label="Add photo"
          >
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
          </button>
        </>
      )}
      {metricId && (
        <span className="text-[11.5px] text-slate-400 ml-0.5">{photos.length}/{maxPerMetric}</span>
      )}

      {/* Upload caption modal */}
      {pendingFile && (
        <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4" onClick={() => !busy && setPendingFile(null)} data-testid="qa-photo-upload-modal">
          <div className="nv-card w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-slate-800 font-semibold">
              <ImageIcon size={18} className="text-sky-700" />
              <h3 className="text-h3">Add a caption (optional)</h3>
            </div>
            <p className="mt-1 text-xs text-slate-500">{pendingFile.name} · {(pendingFile.size / 1024).toFixed(0)}KB before compression</p>
            <textarea
              value={pendingCaption} onChange={(e) => setPendingCaption(e.target.value)}
              rows={3} maxLength={300}
              placeholder="What does this photo show? (e.g. 'Med cabinet locked', 'Egress door clear')"
              className="mt-3 w-full text-sm rounded-lg border border-slate-200 bg-white p-3"
              data-testid="qa-photo-caption-input"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setPendingFile(null)} disabled={busy} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
              <button onClick={doUpload} disabled={busy} className="px-4 py-2 rounded-md text-sm font-semibold bg-sky-700 hover:bg-sky-800 text-white disabled:opacity-50" data-testid="qa-photo-upload-confirm">
                {busy ? "Uploading…" : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox viewer */}
      {viewer && (
        <div className="fixed inset-0 z-[60] bg-slate-900/80 flex items-center justify-center p-4" onClick={() => setViewer(null)} data-testid="qa-photo-viewer">
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setViewer(null)} className="absolute -top-10 right-0 text-white hover:text-slate-200" data-testid="qa-photo-viewer-close"><X size={24} /></button>
            <img src={authedSrc(viewer.image_url)} alt="" className="w-full max-h-[75vh] object-contain rounded-lg bg-black" />
            <div className="mt-3 text-white">
              {viewer.caption && <p className="text-sm">{viewer.caption}</p>}
              <p className="text-xs text-slate-300 mt-1">{viewer.uploaded_by} · {(viewer.uploaded_date || "").slice(0, 10)}</p>
              {canEdit && (
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => { setConfirmDelete(viewer); setViewer(null); }}
                    className="text-rose-300 hover:text-rose-100 text-xs font-semibold"
                    data-testid="qa-photo-delete-btn"
                  >Delete photo</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4" onClick={() => !busy && setConfirmDelete(null)} data-testid="qa-photo-delete-modal">
          <div className="nv-card w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-h3 text-rose-700">Delete this photo?</h3>
            <p className="mt-2 text-sm text-slate-700">This cannot be undone.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} disabled={busy} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
              <button onClick={() => doDelete(confirmDelete.id)} disabled={busy} className="px-4 py-2 rounded-md text-sm font-semibold bg-rose-700 hover:bg-rose-800 text-white disabled:opacity-50" data-testid="qa-photo-delete-confirm">
                {busy ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
